"use client";

import React, { useCallback, useEffect, useState } from "react";
import { Badge } from "../../../../../ui/components/Badge";
import { Button } from "../../../../../ui/components/Button";
import { IconButton } from "../../../../../ui/components/IconButton";
import { IconWithBackground } from "../../../../../ui/components/IconWithBackground";
import { TextArea } from "../../../../../ui/components/TextArea";
import { TextField } from "../../../../../ui/components/TextField";
import { ToggleGroup } from "../../../../../ui/components/ToggleGroup";
import { DialogLayout } from "../../../../../ui/layouts/DialogLayout";
import { FeatherArrowUpRight } from "@subframe/core";
import { FeatherFileText } from "@subframe/core";
import { FeatherPlus } from "@subframe/core";
import { FeatherUser } from "@subframe/core";
import { FeatherX } from "@subframe/core";
import { Select } from "../../../../../ui/components/Select";
import { usersAPI } from "../../../../../services/api";
import { getUser } from "../../../../../shared/data/storage/authStorage";

// Clean Architecture: Use hooks
import { useCreateQuery } from "../hooks/useCreateQuery";
import { useCreateSubQuery } from "../hooks/useCreateSubQuery";
import { QueryPriority, QuerySource } from "../../domain/entities/QueryEntity";

type SourceDocType =
  | "invoice"
  | "bill"
  | "bank"
  | "journal"
  | "credit_note"
  | string;

interface SourceDoc {
  type: SourceDocType;
  id: number;
  filename?: string;
  amount?: string;
  viewUrl?: string;
}

interface QueryInitialValues {
  title?: string;
  description?: string;
  priority?: "Low" | "Medium" | "High";
  assignTo?: string;
}

interface QueryRaiseModalProps {
  open: boolean;
  parentQueryId?: number;
  isCreateSubQuery?: boolean;
  onOpenChange: (open: boolean) => void;
  sourceDoc?: SourceDoc;
  initialValues?: QueryInitialValues;
  onSubmit?: (payload: {
    title: string;
    description: string;
    priority: "Low" | "Medium" | "High";
    assignTo: string;
    sourceDoc?: SourceDoc;
  }) => Promise<void>;
  onQueryRaised?: () => void;
}

const QueryRaiseModal: React.FC<QueryRaiseModalProps> = ({
  open,
  isCreateSubQuery = false,
  parentQueryId,
  onOpenChange,
  initialValues,
  sourceDoc,
  onSubmit,
  onQueryRaised,
}) => {
  const close = useCallback(() => onOpenChange(false), [onOpenChange]);

  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<"Low" | "Medium" | "High">("Medium");
  const [assignTo, setAssignTo] = useState("");
  const [assignToError, setAssignToError] = useState(false);
  const [teamMembers, setTeamMembers] = useState<
    Array<{ id: number; email: string; userName: string }>
  >([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Clean Architecture: Use hooks instead of direct API calls
  const { createQuery, creating: isCreatingQuery } = useCreateQuery();
  const { createSubQuery, creating: isCreatingSubQuery } = useCreateSubQuery();

  const submitting = isCreatingQuery || isCreatingSubQuery;

  // Reset form when modal opens
  useEffect(() => {
    if (!open) return;
    setTitle(initialValues?.title ?? "");
    setDescription(initialValues?.description ?? "");
    setPriority(initialValues?.priority ?? "Medium");
    setAssignTo(initialValues?.assignTo ?? "");
    setAssignToError(false);
  }, [open, initialValues]);

  // Fetch team members
  useEffect(() => {
    if (!open) return;
    let isMounted = true;

    const fetchUsers = async () => {
      try {
        setLoadingUsers(true);
        const data = await usersAPI.getUsers();
        if (!isMounted) return;

        const mapped = (data || []).map((u: any) => ({
          id: Number(u.id),
          email: String(u.email),
          userName: String(u.userName),
        }));
        setTeamMembers(mapped);
      } catch (err) {
        console.error("Failed to fetch users:", err);
      } finally {
        if (isMounted) setLoadingUsers(false);
      }
    };

    fetchUsers();
    return () => {
      isMounted = false;
    };
  }, [open]);

  const handleSubmit = async () => {
    // Validation
    if (!title || title.trim().length < 3) return;
    if (!assignTo) {
      setAssignToError(true);
      return;
    }

    try {
      // Convert priority to lowercase for API
      const priorityApi = priority.toLowerCase() as QueryPriority;

      // Determine source and sourceId
      let source: QuerySource = QuerySource.GENERAL;
      let sourceId: number | null = null;

      if (sourceDoc) {
        const t = String(sourceDoc.type || "").toLowerCase();
        if (t === "bank") source = QuerySource.TRANSACTION;
        else if (t === "invoice") source = QuerySource.INVOICE;
        else if (t === "bill") source = QuerySource.BILL;
        else if (t === "journal") source = QuerySource.JOURNAL;
        else if (t === "credit_note") source = QuerySource.CREDIT_NOTE;

        const parsed = Number(sourceDoc.id);
        sourceId = Number.isFinite(parsed) ? parsed : null;
      }

      const assignedToId = Number(assignTo);

      // If creating a sub-query, use the sub-query hook
      if (isCreateSubQuery && parentQueryId) {
        const result = await createSubQuery({
          parentQueryId,
          title: title.trim(),
          description: description || undefined,
          priority: priorityApi,
          source,
          sourceId: source === "general" ? undefined : sourceId || undefined,
          assignedTo: assignedToId,
          reviewRequired: true,
        });

        if (result.isSuccess) {
          onOpenChange(false);
          onQueryRaised?.();
        } else {
          console.error("Failed to create sub-query:", result.error);
        }
      }
      // If onSubmit prop is provided (for backward compatibility), use it
      else if (onSubmit) {
        await onSubmit({
          title: title.trim(),
          description: description,
          priority: priority,
          assignTo: assignTo,
          sourceDoc: sourceDoc,
        });
        onOpenChange(false);
        onQueryRaised?.();
      }
      // Otherwise, use the create query hook
      else {
        const result = await createQuery({
          title: title.trim(),
          description: description || undefined,
          priority: priorityApi,
          source,
          sourceId:
            source === QuerySource.GENERAL ? undefined : sourceId || undefined,
          assignedTo: assignedToId,
          reviewRequired: true,
        });

        if (result.isSuccess) {
          onOpenChange(false);
          onQueryRaised?.();
        } else {
          console.error("Failed to create query:", result.error);
        }
      }
    } catch (error) {
      console.error("Error submitting query:", error);
    }
  };

  return (
    <DialogLayout open={open} onOpenChange={onOpenChange}>
      <div className="flex w-144 flex-col items-start gap-6 bg-white px-8 py-8">
        <div className="flex w-full items-center justify-between">
          <span className="text-heading-2 font-heading-2 text-default-font">
            {isCreateSubQuery ? "Raise a Sub Query" : "Raise a Query"}
          </span>
          <IconButton icon={<FeatherX />} onClick={close} />
        </div>
        <div className="flex w-full flex-col items-start gap-6">
          <div className="flex w-full flex-col items-start gap-4">
            {sourceDoc ? (
              <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-4">
                <div className="flex w-full items-center justify-between">
                  <div className="flex items-center gap-2">
                    <IconWithBackground icon={<FeatherFileText />} />
                    <span className="text-body-bold font-body-bold text-default-font">
                      {sourceDoc.type === "credit_note" &&
                      (!sourceDoc.filename ||
                        sourceDoc.filename === "credit-note.pdf" ||
                        sourceDoc.filename
                          .toLowerCase()
                          .includes("credit-note"))
                        ? sourceDoc.id
                        : sourceDoc.filename ?? sourceDoc.id}
                    </span>
                  </div>
                  {sourceDoc.viewUrl && sourceDoc.type !== "credit_note" ? (
                    <Button
                      variant="brand-secondary"
                      size="small"
                      icon={<FeatherArrowUpRight />}
                      onClick={() => window.open(sourceDoc.viewUrl!, "_blank")}
                    >
                      View
                    </Button>
                  ) : null}
                </div>
                <div className="flex w-full items-center gap-4">
                  <Badge>
                    {sourceDoc.type === "credit_note"
                      ? "Credit Note"
                      : (sourceDoc.type || "").charAt(0).toUpperCase() +
                        (sourceDoc.type || "").slice(1)}
                  </Badge>
                  {sourceDoc.amount && sourceDoc.type !== "credit_note" ? (
                    <span className="text-body font-body text-neutral-500">
                      Amount: {sourceDoc.amount}
                    </span>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
          <div className="flex w-full flex-col items-start gap-4">
            <span className="text-body-bold font-body-bold text-default-font">
              Query Details
            </span>
            <div className="flex w-full flex-col items-start gap-4">
              <TextField
                className="h-auto w-full flex-none"
                variant="filled"
                label="Query Title"
                helpText="Enter a clear, concise title for the query"
              >
                <TextField.Input
                  placeholder="e.g. Amount mismatch with purchase order"
                  value={title}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setTitle(e.target.value)
                  }
                />
              </TextField>
              <TextArea
                className="h-auto w-full flex-none"
                variant="filled"
                label="Description"
                helpText="Provide additional details about the query"
              >
                <TextArea.Input
                  placeholder="Describe the issue in detail..."
                  value={description}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setDescription(e.target.value)
                  }
                />
              </TextArea>
            </div>
          </div>
          <div className="flex w-full flex-col items-start gap-4">
            <span className="text-body-bold font-body-bold text-default-font">
              Priority &amp; Assignment
            </span>
            <div className="flex w-full flex-col items-start gap-4">
              <div className="flex w-full items-center gap-6">
                <span className="text-body font-body text-subtext-color">
                  Priority Level
                </span>
                <ToggleGroup
                  value={priority}
                  onValueChange={(v: string) =>
                    setPriority(v as "Low" | "Medium" | "High")
                  }
                >
                  <ToggleGroup.Item icon={null} value="Low">
                    Low
                  </ToggleGroup.Item>
                  <ToggleGroup.Item icon={null} value="Medium">
                    Medium
                  </ToggleGroup.Item>
                  <ToggleGroup.Item icon={null} value="High">
                    High
                  </ToggleGroup.Item>
                </ToggleGroup>
              </div>

              <Select
                className="h-auto w-full flex-none"
                variant="filled"
                label="Assign To"
                placeholder={
                  loadingUsers ? "Loading..." : "Select team member..."
                }
                icon={<FeatherUser />}
                error={assignToError}
                helpText={
                  assignToError ? "Please select a team member" : undefined
                }
                value={assignTo}
                onValueChange={setAssignTo}
              >
                {teamMembers
                  .filter((m) => {
                    const currentUser = getUser();
                    // Filter out the current user from the list
                    return currentUser
                      ? String(m.id) !== String(currentUser.id)
                      : true;
                  })
                  .map((m) => (
                    <Select.Item key={m.id} value={String(m.id)}>
                      {m.userName}
                    </Select.Item>
                  ))}
              </Select>
            </div>
          </div>
        </div>
        <div className="flex w-full items-center justify-end gap-2 border-t border-solid border-neutral-border pt-6">
          <Button
            variant="neutral-tertiary"
            onClick={close}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            icon={<FeatherPlus />}
            onClick={handleSubmit}
            loading={submitting}
            disabled={!assignTo || submitting}
          >
            Raise Query
          </Button>
        </div>
      </div>
    </DialogLayout>
  );
};

export default QueryRaiseModal;
